import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import * as authStorage from '../lib/authStorage';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const JWT_SESSION_ERRORS = new Set([
  'Session expirée',
  'Session révoquée',
  'Token expiré',
  'Token invalide',
  'Utilisateur non trouvé',
]);

const shouldLogoutOn401 = (error) => {
  const url = error.config?.url || '';
  if (url.includes('/auth/me')) return true;
  const detail = error.response?.data?.detail;
  const message = typeof detail === 'string' ? detail : '';
  return JWT_SESSION_ERRORS.has(message);
};

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(authStorage.getToken());
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [activeAccount, setActiveAccountState] = useState(authStorage.getActiveAccount() || null);

  const logout = useCallback(() => {
    authStorage.clearAuth();
    delete axios.defaults.headers.common['Authorization'];
    delete axios.defaults.headers.common['X-Account-Email'];
    setToken(null);
    setUser(null);
    setActiveAccountState(null);
  }, []);

  const fetchAccounts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/accounts`);
      setAccounts(response.data);
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
    }
  }, []);

  const setActiveAccount = useCallback((email) => {
    if (email) {
      authStorage.setActiveAccount(email);
      axios.defaults.headers.common['X-Account-Email'] = email;
    } else {
      authStorage.setActiveAccount(null);
      delete axios.defaults.headers.common['X-Account-Email'];
    }
    setActiveAccountState(email);
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      await fetchAccounts();
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [fetchAccounts, logout]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  useEffect(() => {
    if (activeAccount) {
      axios.defaults.headers.common['X-Account-Email'] = activeAccount;
      authStorage.setActiveAccount(activeAccount);
    } else {
      delete axios.defaults.headers.common['X-Account-Email'];
      authStorage.setActiveAccount(null);
    }
  }, [activeAccount]);

  useEffect(() => {
    if (!activeAccount || accounts.length === 0) return;
    const accountExists = accounts.some((account) => account.email === activeAccount);
    if (!accountExists) {
      authStorage.setActiveAccount(null);
      delete axios.defaults.headers.common['X-Account-Email'];
      setActiveAccountState(null);
    }
  }, [accounts, activeAccount]);

  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use((config) => {
      if (config.url?.includes('/auth/login')) return config;
      const account = authStorage.getActiveAccount();
      config.headers = config.headers || {};
      if (account && !config.headers['X-Account-Email']) {
        config.headers['X-Account-Email'] = account;
      }
      return config;
    });
    return () => axios.interceptors.request.eject(requestInterceptor);
  }, []);

  useEffect(() => {
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const authUrl = error.config?.url || '';
        const isAuthEndpoint = authUrl.includes('/auth/login')
          || authUrl.includes('/auth/verify-2fa')
          || authUrl.includes('/auth/recovery-email')
          || authUrl.includes('/auth/totp');
        if (error.response?.status === 401 && !isAuthEndpoint && shouldLogoutOn401(error)) {
          console.warn('Session JWT expirée ou invalide, déconnexion');
          logout();
          globalThis.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(responseInterceptor);
  }, [logout]);

  const applyAuthResponse = useCallback((data) => {
    const { access_token, user: userData } = data;
    if (!access_token || !userData) return null;
    authStorage.setToken(access_token);
    authStorage.setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    if (response.data?.two_factor_required) {
      return response.data;
    }
    return applyAuthResponse(response.data);
  };

  const verifyTwoFactor = async (challengeId, code) => {
    const response = await axios.post(`${API}/auth/verify-2fa`, { challenge_id: challengeId, code });
    if (response.data?.two_factor_required) {
      return response.data;
    }
    return applyAuthResponse(response.data);
  };

  const logoutAllSessions = async () => {
    await axios.post(`${API}/auth/logout-all`);
    logout();
  };

  const register = async (email, password, fullName) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      full_name: fullName
    });
    if (response.data?.two_factor_required) {
      return response.data;
    }
    return applyAuthResponse(response.data);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    verifyTwoFactor,
    logoutAllSessions,
    logout,
    isAuthenticated: !!user,
    accounts,
    activeAccount,
    setActiveAccount,
    fetchAccounts,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
